import { Button } from '@/components/ui/primitives';
import {
    CtaBand,
    FAQList,
    FeatureGrid,
    Hero,
    MarketingPage,
    PageFooter,
    StepList,
    Dateline,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';

const PAGE = pageForRoute('/calibrate-pdf-scale');

const STEPS = [
    {
        title: 'Choose a known length',
        body: 'Start calibration and mark two points at the ends of a labeled dimension or scale-bar segment. Enter the real-world length shown on the drawing.',
        image: '/tool-shots/dimension.jpg',
        alt: 'A dimension on a drawing showing a known length for calibration',
    },
    {
        title: 'Set the scale',
        body: 'Use the two-point calibration or select a metric, imperial architectural, or imperial engineering preset. Choose the unit system used by the drawing.',
        image: '/tool-shots/dimension.jpg',
        alt: 'A calibrated dimension on a PDF drawing',
    },
    {
        title: 'Check another dimension',
        body: 'Measure a second labeled length and compare the result with the value printed on the sheet. If they do not agree, check the endpoints and calibrate again.',
        image: '/tool-shots/dimension.jpg',
        alt: 'A dimension line used to check a PDF drawing scale',
    },
    {
        title: 'Apply the scale where it is needed',
        body: 'Apply a scale to all sheets or a selected range. For a sheet with different viewports or a named region, set a separate scale for that viewport or region.',
        image: '/tool-shots/polygon.jpg',
        alt: 'A measured region on a drawing sheet',
    },
] as const;

const FAQ_ITEMS = [
    {
        question: 'Does a PDF need to have a scale embedded?',
        answer: ['No. You can calibrate by marking two points on a known length and entering its real-world measurement. You can also select a scale preset.'],
    },
    {
        question: 'How can I check a PDF scale?',
        answer: ['After calibration, measure a second known dimension and compare the result with its labeled value.'],
    },
    {
        question: 'Can I apply one scale to multiple sheets?',
        answer: ['Yes. The scale dialog can apply a scale to all sheets or to a selected range.'],
    },
    {
        question: 'Can one sheet have two scales?',
        answer: ['Yes. You can set a separate scale for a PDF viewport or a named region on the page.'],
    },
] as const;

export function CalibratePdfScalePage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical }}>
            <main>
                <Hero
                    eyebrow="Calibrate PDF scale"
                    title={<>Calibrate the scale of a PDF drawing.</>}
                    lead="Set a drawing scale from a known length, check it against another dimension, and apply it to the sheets or regions you plan to measure."
                    cta={<Button href="/" variant="primary" size="lg">Open a PDF plan</Button>}
                    chips={['Two-point calibration', 'Scale presets', 'Imperial and metric']}
                />
                <StepList title="How to calibrate a PDF drawing" lead="Calibrate against a known length, then verify the result before measuring." steps={STEPS} />
                <FeatureGrid
                    sectionTitle="Scale controls for PDF drawings"
                    sectionLead="Use a scale that matches the sheet or the particular view you are measuring."
                    cards={[
                        { title: 'Two-point calibration', body: 'Choose the endpoints of a known length and enter its real-world measurement.' },
                        { title: 'Scale presets', body: 'Choose from metric, imperial architectural, and imperial engineering scale presets.' },
                        { title: 'Sheet, viewport, and region scales', body: 'Apply a scale to all sheets or a range, or set a separate scale for a viewport or named region.' },
                    ]}
                />
                <FAQList items={FAQ_ITEMS} />
                <CtaBand
                    title="Check the scale before measuring"
                    lead="Use a known length for calibration and a second dimension to check the result."
                    actions={<><Button href="/" variant="primary" size="lg">Open a PDF plan</Button><Button href="/drawing-scales/" variant="ghost" size="lg">View drawing scales</Button><Button href="/measure-area-pdf/" variant="ghost" size="lg">Measure PDF area</Button><Button href="/measure-pdf-online/" variant="ghost" size="lg">Measure PDF distances</Button></>}
                />
                <Dateline reviewed={PAGE.reviewed} />
            </main>
            <PageFooter tagline="Calibrate and check PDF drawing scale before measuring" />
        </MarketingPage>
    );
}
